import React from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

function Home() {
  return (
    <div className="bg-gray-900 text-white min-h-screen">
      {/* Hero Section with Checkered Flag */}
      <section 
        className="racing-hero py-20 relative overflow-hidden min-h-[600px] flex items-center"
        style={{
          backgroundImage: 'url(/images/flag.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="max-w-4xl mx-auto text-center px-4 racing-content">
          <div className="mb-8">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white drop-shadow-2xl">
              Welcome to F1 Predictions
            </h1>
            <p className="text-xl md:text-2xl text-gray-100 font-light mb-8 drop-shadow-lg">
              Predict race outcomes and compete with friends throughout the Formula 1 season!
            </p>
          </div>
          
          {/* Status Alert */}
          <div className="bg-yellow-900 bg-opacity-90 border border-yellow-600 rounded-xl p-6 mb-8 backdrop-blur-sm shadow-2xl">
            <div className="flex items-center justify-center space-x-3 mb-3">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
              <h3 className="text-lg font-semibold text-yellow-200">Site Status</h3>
            </div>
            <div className="space-y-2 text-yellow-100">
              <p>This site is in development and will be updated regularly</p>
              <p>If there are any errors in calculating or displaying results, please reach out to <span className="font-medium text-yellow-50">f1predictor.club@gmail.com</span></p>
              <p>For more detail see the 'Roadmap' section</p>
              <p>The most important part is to get your tips in, and as functionality comes online we can reference your submissions for scoring</p>
            </div>
          </div>

          {/* Quick Action Button - More Subtle */}
          <div className="flex justify-center">
            <Link 
              to="/predictions" 
              className="bg-gray-700 hover:bg-gray-600 border-2 border-red-500 hover:border-red-400 px-8 py-4 rounded-xl text-lg font-semibold transition-all transform hover:scale-105 shadow-2xl inline-flex items-center space-x-2 text-white hover:text-red-100"
            >
              <span className="text-2xl">🏁</span>
              <span>Enter My Predictions</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {/* How It Works */}
          <div className="bg-gray-800 rounded-2xl p-8 hover:transform hover:-translate-y-2 transition-all duration-300 border border-gray-700 shadow-xl">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mr-4">
                <span className="text-2xl">⚡</span>
              </div>
              <h2 className="text-2xl font-bold text-white">How It Works</h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-xs font-bold mt-1">1</div>
                <p className="text-gray-300">Create an account to join the competition</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-xs font-bold mt-1">2</div>
                <p className="text-gray-300">Submit your predictions before each race</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-xs font-bold mt-1">3</div>
                <p className="text-gray-300">Earn points for correct predictions</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-xs font-bold mt-1">4</div>
                <p className="text-gray-300">Track your performance on the leaderboard</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-xs font-bold mt-1">5</div>
                <p className="text-gray-300">Compete throughout the entire F1 season</p>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-blue-900 bg-opacity-50 rounded-lg border border-blue-700">
              <p className="text-blue-200 text-sm">
                <span className="font-semibold">Note:</span> Competition is based on feature races only - sprint races are ignored (for now!)
              </p>
            </div>
          </div>

          {/* Prediction Categories */}
          <div className="bg-gray-800 rounded-2xl p-8 hover:transform hover:-translate-y-2 transition-all duration-300 border border-gray-700 shadow-xl">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center mr-4">
                <span className="text-2xl">🎯</span>
              </div>
              <h2 className="text-2xl font-bold text-white">Prediction Categories</h2>
            </div>
            
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-gray-700 to-gray-600 rounded-lg p-4 hover:from-red-800 hover:to-red-700 transition-all duration-300 border border-gray-600 hover:border-red-500">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">🏆</span>
                  <span className="font-medium">Podium Finishers (Top 3)</span>
                </div>
              </div>
              <div className="bg-gradient-to-r from-gray-700 to-gray-600 rounded-lg p-4 hover:from-red-800 hover:to-red-700 transition-all duration-300 border border-gray-600 hover:border-red-500">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">⚡</span>
                  <span className="font-medium">Fastest Lap</span>
                </div>
              </div>
              <div className="bg-gradient-to-r from-gray-700 to-gray-600 rounded-lg p-4 hover:from-red-800 hover:to-red-700 transition-all duration-300 border border-gray-600 hover:border-red-500">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">🥇</span>
                  <span className="font-medium">Pole Position</span>
                </div>
              </div>
              <div className="bg-gradient-to-r from-gray-700 to-gray-600 rounded-lg p-4 hover:from-red-800 hover:to-red-700 transition-all duration-300 border border-gray-600 hover:border-red-500">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">🚩</span>
                  <span className="font-medium">First Retirement / Last Place</span>
                </div>
              </div>
              <div className="bg-gradient-to-r from-gray-700 to-gray-600 rounded-lg p-4 hover:from-red-800 hover:to-red-700 transition-all duration-300 border border-gray-600 hover:border-red-500">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">👥</span>
                  <span className="font-medium">Driver Head to Head</span>
                </div>
              </div>
              <div className="bg-gradient-to-r from-gray-700 to-gray-600 rounded-lg p-4 hover:from-red-800 hover:to-red-700 transition-all duration-300 border border-gray-600 hover:border-red-500">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">🏁</span>
                  <span className="font-medium">Team Head to Head</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Important Notice */}
        <div className="bg-gradient-to-r from-red-900 to-red-800 rounded-2xl p-8 text-center border border-red-600 shadow-2xl mb-16">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center">
              <span className="text-3xl">⏰</span>
            </div>
          </div>
          <h3 className="text-2xl font-bold mb-4 text-white">Prediction Deadline</h3>
          <p className="text-xl text-red-100 mb-4">
            Predictions close <span className="font-bold text-white">2 minutes before qualifying starts</span>
          </p>
        </div>

        {/* Points Breakdown */}
        <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700 shadow-xl">
          <div className="flex items-center mb-8">
            <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center mr-4">
              <span className="text-2xl">📊</span>
            </div>
            <h2 className="text-3xl font-bold text-white">Points Breakdown</h2>
          </div>
          <p className="text-gray-300 mb-8 text-lg">Here's how each category is scored:</p>
          
          <div className="space-y-8">
            {/* Podium Predictions */}
            <div className="bg-gray-700 rounded-xl p-6 border border-gray-600">
              <h3 className="font-bold text-xl mb-4 text-yellow-400 flex items-center">
                <span className="text-2xl mr-2">🏆</span>
                Podium Predictions
              </h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center text-xs font-bold text-black">2</div>
                  <div>
                    <strong className="text-yellow-300">Pole (2 points):</strong>
                    <span className="text-gray-300 ml-2">As listed on official results as finishing in 1st place in qualifying on the official classification results.</span>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center text-xs font-bold text-black">6</div>
                  <div>
                    <strong className="text-yellow-300">Winning Driver (6 points):</strong>
                    <span className="text-gray-300 ml-2">As listed on official results as finishing in 1st place in the race.</span>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center text-xs font-bold text-black">4</div>
                  <div>
                    <strong className="text-yellow-300">Race 2nd (4 points):</strong>
                    <span className="text-gray-300 ml-2">Predicting the driver who finishes the race in second place.</span>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center text-xs font-bold text-black">2</div>
                  <div>
                    <strong className="text-yellow-300">Race 3rd (2 points):</strong>
                    <span className="text-gray-300 ml-2">Predicting the driver who finishes the race in third place.</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Race Performance */}
            <div className="bg-gray-700 rounded-xl p-6 border border-gray-600">
              <h3 className="font-bold text-xl mb-4 text-blue-400 flex items-center">
                <span className="text-2xl mr-2">⚡</span>
                Race Performance
              </h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-xs font-bold text-white">1</div>
                  <div>
                    <strong className="text-blue-300">Fastest Lap (1 point):</strong>
                    <span className="text-gray-300 ml-2">Predicting the driver who sets the fastest lap time during the race.</span>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-xs font-bold text-white">1</div>
                  <div>
                    <strong className="text-blue-300">First retirement/ last place (1 point):</strong>
                    <span className="text-gray-300 ml-2">Predicting the driver who finishes in last place as listed on the official classification results.</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Head-to-Head */}
            <div className="bg-gray-700 rounded-xl p-6 border border-gray-600">
              <h3 className="font-bold text-xl mb-4 text-purple-400 flex items-center">
                <span className="text-2xl mr-2">👥</span>
                Head-to-Head Competitions
              </h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-xs font-bold text-white">1</div>
                  <div>
                    <strong className="text-purple-300">Driver Head to Head (1 point):</strong>
                    <span className="text-gray-300 ml-2">Which driver will finish higher - based on the official classification results. If the 2 drivers crash and take each other out, the winner is the one listed above the other in official results.</span>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-xs font-bold text-white">1</div>
                  <div>
                    <strong className="text-purple-300">Team Head to Head (1 point):</strong>
                    <span className="text-gray-300 ml-2">Which team will have the higher average finishing position (add both driver positions and divide by 2). E.g. If Team A finished 1st and 20th, and Team B finished 9th and 10th, Team B will win as their ave position was 9.5, Team A's ave was 10.5.</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Bonus Points */}
            <div className="bg-gray-700 rounded-xl p-6 border border-gray-600">
              <h3 className="font-bold text-xl mb-4 text-green-400 flex items-center">
                <span className="text-2xl mr-2">🎁</span>
                Bonus Points
              </h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-xs font-bold text-white">2</div>
                  <div>
                    <strong className="text-green-300">Top 3 any order (2 bonus points):</strong>
                    <span className="text-gray-300 ml-2">Predicting the three drivers who will finish in the top three positions, regardless of their exact finishing order.</span>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-xs font-bold text-white">6</div>
                  <div>
                    <strong className="text-green-300">Top 3 in order (6 bonus points):</strong>
                    <span className="text-gray-300 ml-2">Predicting the exact finishing order of the top three drivers (1st, 2nd, and 3rd).</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default Home;